import {
  useListOrganizations,
  getListOrganizationsQueryKey,
  useCreateOrganization,
} from '@np-forge/api-client/organizations'
import { useState, type FC, type MouseEvent as ReactMouseEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { queryClient } from '@/api/client'
import { AnalyticsIcon, MembersIcon, MyPathIcon, ScenarioLibraryIcon, SettingsIcon } from '@/components/core/Icon/icons'
import { SIDEBAR_TEST_IDS } from '@/constants/testIds'
import { useLeaveModal } from '@/context/LeaveModalContext'
import { ROUTES } from '@/routes/paths'
import { myPathShownService } from '@/services/myPathShown.service'

import { CreateOrganizationModal } from './CreateOrganizationModal'
import { OrganizationDropdown } from './OrganizationDropdown'
import { SidebarFrame } from './SidebarFrame'

interface SidebarProps {
  userEmail: string
  organizationId: string
  initialOpen?: boolean
  isOpen?: boolean
  onToggle?: () => void
  onLogout: () => void
}

/** The original portal's sidebar: its workspaces and their pages. The B2B portal has its own (`B2bSidebar`). */
export const Sidebar: FC<SidebarProps> = ({
  userEmail,
  organizationId,
  initialOpen,
  isOpen: isOpenProp,
  onToggle,
  onLogout,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { confirmNavigation } = useLeaveModal()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const isOpen = typeof isOpenProp === 'boolean' ? isOpenProp : (initialOpen ?? true)
  const isMyPathShown = myPathShownService.isActive()

  // Fetch organizations
  const { data: organizationsData } = useListOrganizations()

  const workspaces = organizationsData?.organizations || []
  const currentWorkspace = workspaces.find(org => org.id === organizationId)

  // Create organization mutation
  const createOrgMutation = useCreateOrganization<Error>({
    mutation: {
      onSuccess: data => {
        void queryClient.invalidateQueries({ queryKey: getListOrganizationsQueryKey() })
        setIsModalOpen(false)
        void navigate(ROUTES.WORKSPACE_LIBRARY.buildPath({ id: data.id }))
      },
    },
  })

  // Navigation handlers
  const handleNavigateToLibrary = () => {
    confirmNavigation(() => navigate(ROUTES.WORKSPACE_LIBRARY.buildPath({ id: organizationId })), {
      title: 'Back to Scenario library',
      description:
        'The progress you did when edited will be lost. Are you sure you want to go back to the scenario library?',
      confirmText: 'Back to library',
      cancelText: 'Cancel',
    })
  }

  const handleNavigateToSettings = () => {
    confirmNavigation(() => navigate(ROUTES.WORKSPACE_SETTINGS.buildPath({ id: organizationId })), {
      title: leaveTitle,
      description: 'The progress you did when edited will be lost. Are you sure you want to leave this page?',
      confirmText: 'Leave page',
      cancelText: 'Cancel',
    })
  }

  const handleNavigateToWorkspaceMembers = () => {
    confirmNavigation(() => navigate(ROUTES.WORKSPACE_MEMBERS.buildPath({ id: organizationId })), {
      title: leaveTitle,
      description: 'The progress you did when edited will be lost. Are you sure you want to leave this page?',
      confirmText: 'Leave page',
      cancelText: 'Cancel',
    })
  }

  const handleNavigateToAnalytics = () => {
    confirmNavigation(() => navigate(ROUTES.ANALYTICS.buildPath({ id: organizationId })), {
      title: leaveTitle,
      description: 'The progress you did when edited will be lost. Are you sure you want to leave this page?',
      confirmText: 'Leave page',
      cancelText: 'Cancel',
    })
  }

  const handleNavigateToMyPath = () => {
    confirmNavigation(() => navigate(ROUTES.MY_PATH.buildPath({ id: organizationId })), {
      title: leaveTitle,
      description: 'The progress you did when edited will be lost. Are you sure you want to leave this page?',
      confirmText: 'Leave page',
      cancelText: 'Cancel',
    })
  }

  const handleNavigateToAccountSettings = () => {
    void navigate(ROUTES.ACCOUNT_SETTINGS.buildPath({ id: organizationId }))
  }

  // Determine active navigation item
  const isLibraryActive = location.pathname.includes('/library')
  const isMyPathActive = location.pathname.includes('/my-path')
  const isAnalyticsActive = location.pathname.includes('/analytics')
  const isSettingsActive = location.pathname.includes('/settings')
  const isMembersActive = location.pathname.includes('/workspace-members')
  // Show leave-confirm only on create/edit scenario routes
  const isCreateScenarioRoute = location.pathname.includes('/create-scenario')
  const isEditScenarioRoute = location.pathname.includes('/edit-scenario')
  const isScenarioEditingRoute = isCreateScenarioRoute || isEditScenarioRoute
  const leaveTitle = isCreateScenarioRoute ? 'Leave create scenario' : 'Leave edit scenario'

  // Helper: only intercept plain left-clicks (preserve Cmd/Ctrl/Shift and middle-click behaviors)
  const isPlainLeftClick = (e: ReactMouseEvent) =>
    e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey

  const handleCreateOrganization = (name: string) => {
    createOrgMutation.mutate({ data: { name, default_scenario_aliases: [] } })
  }

  return (
    <>
      <SidebarFrame
        isOpen={isOpen}
        onToggle={onToggle}
        workspaceName={currentWorkspace?.name || 'Workspace'}
        renderWorkspaceDropdown={closeDropdown => (
          <OrganizationDropdown
            organizations={workspaces}
            currentOrganizationId={organizationId}
            isOpen
            onCreateClick={() => {
              closeDropdown()
              setIsModalOpen(true)
            }}
            onClose={closeDropdown}
          />
        )}
        nav={
          <>
            <Link
              to={ROUTES.WORKSPACE_LIBRARY.buildPath({ id: organizationId })}
              className={`sidebar__nav-item ${isLibraryActive ? 'sidebar__nav-item--active' : ''}`}
              aria-current={isLibraryActive ? 'page' : undefined}
              title={!isOpen ? 'Scenario library' : ''}
              onClick={(e: ReactMouseEvent) => {
                if (isScenarioEditingRoute && isPlainLeftClick(e)) {
                  e.preventDefault()
                  handleNavigateToLibrary()
                }
              }}
              data-testid={SIDEBAR_TEST_IDS.NAV_SCENARIO_LIBRARY}
            >
              <ScenarioLibraryIcon />
              <span className="sidebar__nav-label">Scenario library</span>
            </Link>
            {isMyPathShown && (
              <Link
                to={ROUTES.MY_PATH.buildPath({ id: organizationId })}
                className={`sidebar__nav-item ${isMyPathActive ? 'sidebar__nav-item--active' : ''}`}
                aria-current={isMyPathActive ? 'page' : undefined}
                title={!isOpen ? 'My path' : ''}
                onClick={(e: ReactMouseEvent) => {
                  if (isScenarioEditingRoute && isPlainLeftClick(e)) {
                    e.preventDefault()
                    handleNavigateToMyPath()
                  }
                }}
                data-testid={SIDEBAR_TEST_IDS.NAV_MY_PATH}
              >
                <MyPathIcon />
                <span className="sidebar__nav-label">My path</span>
              </Link>
            )}
            {currentWorkspace?.my_workspace_capabilities?.can_view_analytics && (
              <Link
                to={ROUTES.ANALYTICS.buildPath({ id: organizationId })}
                className={`sidebar__nav-item ${isAnalyticsActive ? 'sidebar__nav-item--active' : ''}`}
                aria-current={isAnalyticsActive ? 'page' : undefined}
                title={!isOpen ? 'Analytics' : ''}
                onClick={(e: ReactMouseEvent) => {
                  if (isScenarioEditingRoute && isPlainLeftClick(e)) {
                    e.preventDefault()
                    handleNavigateToAnalytics()
                  }
                }}
                data-testid={SIDEBAR_TEST_IDS.NAV_ANALYTICS}
              >
                <AnalyticsIcon />
                <span className="sidebar__nav-label">Analytics</span>
              </Link>
            )}
          </>
        }
        bottomItems={
          <>
            {currentWorkspace?.my_workspace_capabilities?.can_manage_members && (
              <Link
                to={ROUTES.WORKSPACE_MEMBERS.buildPath({ id: organizationId })}
                className={`sidebar__bottom-item ${isMembersActive ? 'sidebar__bottom-item--active' : ''}`}
                aria-current={isMembersActive ? 'page' : undefined}
                title={!isOpen ? 'Workspace members' : ''}
                onClick={(e: ReactMouseEvent) => {
                  if (isScenarioEditingRoute && isPlainLeftClick(e)) {
                    e.preventDefault()
                    handleNavigateToWorkspaceMembers()
                  }
                }}
                data-testid={SIDEBAR_TEST_IDS.NAV_WORKSPACE_MEMBERS}
              >
                <MembersIcon />
                <span className="sidebar__nav-label">Workspace members</span>
              </Link>
            )}
            {currentWorkspace?.my_workspace_capabilities?.can_manage_workspace && (
              <Link
                to={ROUTES.WORKSPACE_SETTINGS.buildPath({ id: organizationId })}
                className={`sidebar__bottom-item ${isSettingsActive ? 'sidebar__bottom-item--active' : ''}`}
                aria-current={isSettingsActive ? 'page' : undefined}
                title={!isOpen ? 'Workspace settings' : ''}
                onClick={(e: ReactMouseEvent) => {
                  if (isScenarioEditingRoute && isPlainLeftClick(e)) {
                    e.preventDefault()
                    handleNavigateToSettings()
                  }
                }}
                data-testid={SIDEBAR_TEST_IDS.NAV_WORKSPACE_SETTINGS}
              >
                <SettingsIcon />
                <span className="sidebar__nav-label">Workspace settings</span>
              </Link>
            )}
          </>
        }
        userEmail={userEmail}
        onLogout={onLogout}
        onMyAccount={handleNavigateToAccountSettings}
        termsOfServicePath={ROUTES.TERMS_OF_SERVICE.fullPath}
      />

      {/* Create Organization Modal - rendered OUTSIDE sidebar for proper z-index stacking */}
      <CreateOrganizationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateOrganization}
        isLoading={createOrgMutation.isPending}
        error={createOrgMutation.error?.message || null}
      />
    </>
  )
}
